import { css } from '@emotion/react';
import {
  black,
  genAIGradientType1,
  gray1,
  gray2,
  gray3,
  gray4,
  gray6,
  hexToRgbaString,
  kelp,
  primary,
  success,
  warning,
  white,
} from 'styles/global_defaults/colors';
import {
  doubleSpacing,
  extraLargeSpacing,
  halfSpacing,
  largeSpacing,
  quarterSpacing,
  standardSpacing,
  threeQuartersSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import {
  screenMobileMax,
  screenSmMax,
  screenXsMax,
} from 'styles/global_defaults/media-queries';
import {
  boldFontWeight,
  headerLineHeight,
  lightFontWeight,
  normalFontWeight,
  openSans,
  openSansCondensed,
  semiBoldFontWeight,
  textLargeBodyFontSize,
  textMediumFontSize,
  textMediumLineHeight,
} from 'styles/global_defaults/fonts';

export const compactLayoutMaxWidth = screenXsMax + 1;
const statsCardCount = 5;
const statsCardWidth = 180;
export const statsRowMaxWidth = (statsCardCount * statsCardWidth)
  + ((statsCardCount - 1) * halfSpacing);

const fullPageStyles = css`
  position: relative;
  min-height: 100vh;
  background: ${genAIGradientType1}
  overflow: hidden;
  padding: ${4 * standardSpacing}px ${standardSpacing}px ${extraLargeSpacing}px;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    aspect-ratio: 1280 / 680.914;
    background: ${genAIGradientType1}
    /* Keep the existing wave silhouette, expressed as percentages so it scales with the report. */
    clip-path: polygon(
      0 0,
      100% 0,
      100% 15.93%,
      99.53% 26.88%,
      98.19% 36.11%,
      96.06% 43.87%,
      93.23% 50.4%,
      89.79% 55.93%,
      85.94% 60.35%,
      81.87% 63.46%,
      77.57% 65.38%,
      73.01% 66.23%,
      68.19% 66.11%,
      63.16% 64.91%,
      58.16% 62.92%,
      53.22% 60.55%,
      48.38% 58.21%,
      43.68% 56.3%,
      37.66% 55.09%,
      31.95% 55.66%,
      26.55% 57.96%,
      21.45% 61.96%,
      16.67% 67.59%,
      13.83% 75.09%,
      10.63% 82.44%,
      7.19% 89.29%,
      3.61% 95.26%,
      0 100%
    );
    z-index: 0;
  }

  .takeaways-content {
    position: relative;
    z-index: 1;
    max-width: ${statsRowMaxWidth}px;
    margin: 0 auto;
    text-align: center;
  }

  .mobile-burger-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 56px;
    display: none;
    align-items: center;
    padding: 0 ${halfSpacing}px;
    background: transparent;
    transition: background-color 120ms ease, box-shadow 120ms ease;
    z-index: 3;

    &.scrolled {
      background: ${white};
      box-shadow: 0 1px 0 0 ${gray4};
    }
  }

  .mobile-burger-button {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 6px;
    background: transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .takeaways-spark {
    display: inline-flex;
    margin-bottom: ${largeSpacing}px;
  }

  .eyebrow {
    margin-bottom: ${standardSpacing}px;
  }

  .main-title {
    font-family: ${openSansCondensed};
    margin-bottom: ${tripleSpacing}px;
  }

  .divider {
    width: 100px;
    height: 1px;
    background: ${gray4};
    margin: 0 auto 44px;
  }

  .section {
    margin-bottom: 72px;

    .section-kicker {
      font-weight: ${boldFontWeight};
      margin-bottom: ${halfSpacing}px;
    }

    .section-title {
      color: ${gray1};
      font-family: ${openSans};
      font-weight: ${lightFontWeight};
      margin-bottom: ${standardSpacing}px;
    }

    .keep-learning-empty {
      color: ${gray1};
      font-family: ${openSans};
      font-size: ${textLargeBodyFontSize}px;
      font-weight: ${normalFontWeight};
      line-height: ${headerLineHeight}px;
      text-align: center;
    }

    .section-empty {
      text-align: center;
    }

    .section-copy {
      width: 100%;
      max-width: 800px;
      margin: 0 auto;
      text-align: left;

      .section-headline,
      .section-summary,
      .section-intro,
      .section-empty {
        color: ${gray1};
      }

      .section-headline {
        margin-bottom: ${halfSpacing}px;
      }

      .section-intro {
        margin-top: ${halfSpacing}px;
      }

      .section-bullets {
        color: ${black};
        text-align: left;
        margin: ${standardSpacing}px auto 0;
        padding-left: 0;
        max-width: 800px;
        list-style: none;

        li {
          display: flex;
          align-items: center;
          gap: ${standardSpacing}px;
          padding: 0 ${standardSpacing}px;
        }

        li + li {
          margin-top: ${largeSpacing}px;
        }
      }

      .section-bullet-icon {
        color: ${warning};
        flex-shrink: 0;
      }

      .section-bullet-text {
        flex: 1;
      }

      .section-resources {
        margin-top: ${standardSpacing}px;
        display: grid;
        gap: ${largeSpacing}px;

        .resource-card {
          text-align: left;
          display: flex;
          align-items: center;
          gap: ${standardSpacing}px;
          padding: 0 ${standardSpacing}px;

          .resource-icon {
            color: ${warning};
            flex-shrink: 0;
          }

          .resource-title {
            color: ${primary};
            text-decoration: underline;
          }

          .resource-reason {
            color: ${gray1};
            display: inline;
          }
        }
      }
    }
  }

  .generating-block {
    gap: ${quarterSpacing}px;
    min-height: 70px;
  }

  .generating-label {
    color: ${gray2};
  }

  .report-error-state {
    min-height: 360px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .report-error-icon {
    width: 100px;
    height: 100px;
    color: ${gray4};
    line-height: 100px;
    padding: 0;
    text-align: center;
    margin-bottom: ${largeSpacing}px;
  }

  .insufficient-content-icon {
    width: 100px;
    height: 100px;
    line-height: 100px;
    padding: 0;
    text-align: center;
    color: ${gray4};
    margin-bottom: ${largeSpacing}px;
  }

  .report-error-message {
    max-width: 620px;
    margin: 0 auto ${largeSpacing}px;
    color: ${gray1};
  }

  .generation-error-message {
    width: 100%;
    max-width: 800px;
    color: ${gray3};
    font-family: ${openSans};
    font-size: ${textMediumFontSize}px;
    font-weight: ${boldFontWeight};
    line-height: ${textMediumLineHeight}px;
    letter-spacing: 0;
    text-align: center;
  }

  .insufficient-content-message {
    width: 800px;
    max-width: 100%;
    color: ${gray3};
    font-family: ${openSans};
    font-size: ${textMediumFontSize}px;
    font-weight: ${boldFontWeight};
    line-height: ${textMediumLineHeight}px;
    letter-spacing: 0;
    text-align: center;
  }

  .report-error-state .back-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: ${openSans};
    font-size: ${textMediumFontSize}px;
    font-weight: ${semiBoldFontWeight};
    line-height: ${headerLineHeight}px;
    letter-spacing: 0;
  }

  .stats-row {
    flex-wrap: wrap;
    justify-content: center;
    gap: ${halfSpacing}px;
    margin-top: ${standardSpacing}px;
  }

  .stats-card {
    flex: 0 0 ${statsCardWidth}px;
    width: ${statsCardWidth}px;
    min-width: ${statsCardWidth}px;
    max-width: ${statsCardWidth}px;
    min-height: 92px;
    border-radius: ${halfSpacing}px;
    box-shadow: 0 ${halfSpacing}px ${largeSpacing}px ${hexToRgbaString(gray6, 0.5)};
    background: ${hexToRgbaString(white, 0.5)};
    padding: ${halfSpacing}px ${standardSpacing}px ${standardSpacing}px;
    gap: ${halfSpacing}px;
  }

  .stat-value {
    color: ${kelp};
  }

  .stat-value,
  .stat-label {
    font-weight: ${boldFontWeight};
  }

  .mini-spinner {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid ${hexToRgbaString(gray2, 0.2)};
    border-top-color: ${hexToRgbaString(gray2, 0.8)};
    animation: spin 0.9s linear infinite;
  }

  .completion-badge {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: ${success};
    margin: 0 auto ${standardSpacing}px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    .congrats-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .completion-copy {
    max-width: 620px;
    margin: 0 auto;

    .section-title {
      margin-bottom: ${standardSpacing}px;
    }

    .completion-body {
      color: ${gray1};
      margin-bottom: ${largeSpacing}px;
    }
  }

  .back-button {
    min-height: 35px;
    border: none;
    border-radius: ${quarterSpacing}px;
    background: ${primary};
    color: ${white};
    padding: ${quarterSpacing}px ${threeQuartersSpacing}px;
  }

  .note {
    margin-top: ${largeSpacing}px;
    color: ${gray2};
  }

  @media (max-width: ${compactLayoutMaxWidth}px) {
    padding: ${4 * standardSpacing}px ${halfSpacing}px ${doubleSpacing}px;

    .main-title {
      margin-bottom: ${doubleSpacing}px;
    }
  }

  @media (max-width: ${screenSmMax}px) {
    .mobile-burger-bar {
      display: flex;
    }
  }

  @media (max-width: ${screenMobileMax}px) {
    .section .section-copy {
      .section-bullets li {
        align-items: center;
        flex-direction: column;
        gap: ${halfSpacing}px;
      }

      .section-bullet-text {
        align-self: stretch;
        text-align: left;
      }

      .section-resources .resource-card {
        align-items: center;
        flex-direction: column;
        gap: ${halfSpacing}px;
      }

      .resource-text {
        align-self: stretch;
        text-align: left;
      }
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default fullPageStyles;
